import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'

export interface UserAttributes {
  favorite_workflows?: Record<string, string[]>  // e.g., {"n8n": ["workflow-id-1", "workflow-id-2"]}
  [key: string]: unknown  // Allow other attributes
}

export interface User {
  id: number
  email: string
  name: string
  scopes: string[]
  attributes: UserAttributes
}

interface LoginResponse {
  access_token: string
  user: User
}

interface RefreshResponse {
  access_token: string
}

interface AzureStatusResponse {
  configured: boolean
  login_url: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const azureConfigured = ref<boolean | null>(null)
  const azureLoginUrl = ref<string | null>(null)

  // Scope Testing Mode State (admin feature)
  const isTestingMode = ref(false)
  const testingScopes = ref<string[]>([])

  // Restore testing mode from sessionStorage on init
  const savedTestingMode = sessionStorage.getItem('scopeTestingMode')
  if (savedTestingMode) {
    try {
      const parsed = JSON.parse(savedTestingMode)
      isTestingMode.value = parsed.enabled || false
      testingScopes.value = parsed.scopes || []
    } catch {
      // Invalid data, ignore
    }
  }

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value)

  // Check if user is admin (has wildcard scope in their REAL scopes)
  const isAdmin = computed(() => user.value?.scopes.includes('*') || false)

  // Get effective scopes (testing scopes if in testing mode, otherwise real scopes)
  const effectiveScopes = computed(() =>
    isTestingMode.value ? testingScopes.value : user.value?.scopes || []
  )

  /**
   * Check if user has a specific scope.
   * Implements fallback logic matching backend:
   * 1. Check for admin wildcard (*)
   * 2. Check for exact scope match
   * 3. For folder-scoped resources, fall back to global scope
   *    e.g., workflows:esg:read falls back to workflows:read
   *
   * Scope format: resource:folder:action
   * - workflows:esg:read - read workflows in 'esg' folder
   * - workflows:demo:execute - execute workflows in 'demo' folder
   * - templates:esg:write - write templates in 'esg' folder
   */
  const hasScope = (scope: string): boolean => {
    if (!user.value) return false

    const scopes = effectiveScopes.value

    // Check for admin wildcard
    if (scopes.includes('*')) return true

    // Check for exact match
    if (scopes.includes(scope)) return true

    // Implement fallback logic for hierarchical scopes
    // Format: resource:folder:action -> falls back to resource:action
    const parts = scope.split(':')
    if (parts.length === 3) {
      const [resource, , action] = parts
      const globalScope = `${resource}:${action}`
      if (scopes.includes(globalScope)) return true
    }

    return false
  }

  /**
   * Check if user has access to a specific folder for a resource/action.
   * Returns true if user has:
   * - Admin wildcard (*)
   * - Global scope (resource:action) - access to ALL folders
   * - Folder-specific scope (resource:folder:action)
   *
   * @param resource - 'workflows' or 'templates'
   * @param folder - The workflow folder name (e.g., 'esg', 'demo')
   * @param action - 'read', 'write', 'execute', etc.
   */
  const hasFolderScope = (resource: string, folder: string | undefined | null, action: string): boolean => {
    if (!user.value) return false

    const scopes = effectiveScopes.value

    // Check for admin wildcard
    if (scopes.includes('*')) return true

    // Check for global scope (access to all folders)
    const globalScope = `${resource}:${action}`
    if (scopes.includes(globalScope)) return true

    // If no folder specified, only global scope grants access
    if (!folder) return false

    // Check for folder-specific scope
    const folderScope = `${resource}:${folder}:${action}`
    return scopes.includes(folderScope)
  }

  /**
   * Get list of folders user has access to for a given resource/action.
   * Returns null if user has global access (all folders).
   * Returns empty array if user has no access.
   * Returns array of folder names if user has folder-specific access.
   *
   * @param resource - 'workflows' or 'templates'
   * @param action - 'read', 'write', 'execute', etc.
   */
  const getAccessibleFolders = (resource: string, action: string): string[] | null => {
    if (!user.value) return []

    const scopes = effectiveScopes.value

    // Admin or global scope = access to all folders
    if (scopes.includes('*')) return null
    if (scopes.includes(`${resource}:${action}`)) return null

    // Extract folder-specific scopes
    const folders: string[] = []
    const pattern = new RegExp(`^${resource}:([^:]+):${action}$`)

    for (const scope of scopes) {
      const match = scope.match(pattern)
      if (match) {
        folders.push(match[1])
      }
    }

    return folders
  }

  /**
   * Check if user has any access to a resource/action (global or folder-specific).
   * Useful for showing/hiding UI elements like upload buttons.
   *
   * @param resource - 'workflows' or 'templates'
   * @param action - 'read', 'write', 'execute', etc.
   */
  const hasAnyFolderAccess = (resource: string, action: string): boolean => {
    const folders = getAccessibleFolders(resource, action)
    // null means global access, array means specific folders (or empty = no access)
    return folders === null || folders.length > 0
  }

  // Get all favorite workflows
  const favoriteWorkflows = computed(() => {
    return user.value?.attributes?.favorite_workflows || {}
  })

  // Check if a specific workflow is favorited
  const isWorkflowFavorited = (engineType: string, workflowId: string): boolean => {
    const favorites = user.value?.attributes?.favorite_workflows
    if (!favorites) return false
    return favorites[engineType]?.includes(workflowId) || false
  }

  // Actions
  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/api/auth/login', { email, password })

      accessToken.value = response.data.access_token
      user.value = response.data.user

      // Store token in localStorage for persistence
      localStorage.setItem('accessToken', response.data.access_token)

      // Sync token with API client
      api.setAccessToken(response.data.access_token)

      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Login failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    isLoading.value = true

    try {
      await api.post('/api/auth/logout')
    } catch {
      // Ignore errors during logout
    } finally {
      // Clear state regardless of API result
      user.value = null
      accessToken.value = null
      localStorage.removeItem('accessToken')

      // Clear API client token
      api.setAccessToken(null)

      isLoading.value = false
    }
  }

  async function checkAuth(): Promise<boolean> {
    if (!accessToken.value) {
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await api.get<User>('/api/auth/me')
      user.value = response.data
      return true
    } catch {
      // Token invalid, try refresh
      const refreshed = await refreshToken()
      if (refreshed) {
        return checkAuth()
      }

      // Clear invalid token
      user.value = null
      accessToken.value = null
      localStorage.removeItem('accessToken')
      api.setAccessToken(null)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function refreshToken(): Promise<boolean> {
    try {
      const response = await api.post<RefreshResponse>('/api/auth/refresh')
      accessToken.value = response.data.access_token
      localStorage.setItem('accessToken', response.data.access_token)

      // Sync token with API client
      api.setAccessToken(response.data.access_token)

      return true
    } catch {
      return false
    }
  }

  function setAccessToken(token: string | null): void {
    accessToken.value = token
    if (token) {
      localStorage.setItem('accessToken', token)
    } else {
      localStorage.removeItem('accessToken')
    }
    // Sync with API client
    api.setAccessToken(token)
  }

  // Refresh user data from API (including attributes/favorites)
  async function refreshUserData(): Promise<boolean> {
    if (!accessToken.value) return false

    try {
      const response = await api.get<User>('/api/auth/me')
      user.value = response.data
      return true
    } catch {
      return false
    }
  }

  // Update local favorite status (for optimistic updates)
  function updateLocalFavorite(engineType: string, workflowId: string, isFavorited: boolean): void {
    if (!user.value) return

    // Ensure attributes and favorite_workflows exist
    if (!user.value.attributes) {
      user.value.attributes = {}
    }
    if (!user.value.attributes.favorite_workflows) {
      user.value.attributes.favorite_workflows = {}
    }
    if (!user.value.attributes.favorite_workflows[engineType]) {
      user.value.attributes.favorite_workflows[engineType] = []
    }

    const favorites = user.value.attributes.favorite_workflows[engineType]

    if (isFavorited && !favorites.includes(workflowId)) {
      favorites.push(workflowId)
    } else if (!isFavorited) {
      const index = favorites.indexOf(workflowId)
      if (index > -1) {
        favorites.splice(index, 1)
      }
    }
  }

  // Check if Azure/Entra authentication is configured
  async function checkAzureStatus(): Promise<boolean> {
    try {
      const response = await api.get<AzureStatusResponse>('/api/auth/azure/status')
      azureConfigured.value = response.data.configured
      azureLoginUrl.value = response.data.login_url
      return response.data.configured
    } catch {
      azureConfigured.value = false
      azureLoginUrl.value = null
      return false
    }
  }

  // Exchange existing session cookie for JWT tokens (used after Azure login)
  async function exchangeSessionForJwt(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<LoginResponse>('/api/auth/session-exchange')

      accessToken.value = response.data.access_token
      user.value = response.data.user

      // Store token in localStorage for persistence
      localStorage.setItem('accessToken', response.data.access_token)

      // Sync token with API client
      api.setAccessToken(response.data.access_token)

      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Session exchange failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Initiate Azure login flow (redirects to Microsoft)
  function initiateAzureLogin(returnUrl?: string): void {
    if (!azureLoginUrl.value) {
      error.value = 'Azure login is not configured'
      return
    }

    // Store the return URL so we can redirect after login
    const redirect = returnUrl || window.location.pathname
    sessionStorage.setItem('azure_return_url', redirect)

    // Redirect to Azure login with return URL pointing to SPA
    const loginUrl = new URL(azureLoginUrl.value, window.location.origin)
    loginUrl.searchParams.set('redirect_url', '/app/auth/callback')

    window.location.href = loginUrl.toString()
  }

  // Enable scope testing mode (admin only)
  function enableScopeTesting(scopes: string[]): boolean {
    // Only admins can use testing mode
    if (!isAdmin.value) return false

    testingScopes.value = scopes
    isTestingMode.value = true

    // Persist to sessionStorage
    sessionStorage.setItem('scopeTestingMode', JSON.stringify({
      enabled: true,
      scopes: scopes
    }))

    return true
  }

  // Disable scope testing mode
  function disableScopeTesting(): void {
    isTestingMode.value = false
    testingScopes.value = []
    sessionStorage.removeItem('scopeTestingMode')
  }

  // Update testing scopes without disabling
  function updateTestingScopes(scopes: string[]): void {
    if (!isTestingMode.value) return
    testingScopes.value = scopes
    sessionStorage.setItem('scopeTestingMode', JSON.stringify({
      enabled: true,
      scopes: scopes
    }))
  }

  return {
    // State
    user,
    accessToken,
    isLoading,
    error,
    azureConfigured,
    azureLoginUrl,

    // Scope Testing State
    isTestingMode,
    testingScopes,

    // Getters
    isAuthenticated,
    isAdmin,
    effectiveScopes,
    hasScope,
    hasFolderScope,
    getAccessibleFolders,
    hasAnyFolderAccess,
    favoriteWorkflows,
    isWorkflowFavorited,

    // Actions
    login,
    logout,
    checkAuth,
    refreshToken,
    setAccessToken,
    refreshUserData,
    updateLocalFavorite,
    checkAzureStatus,
    exchangeSessionForJwt,
    initiateAzureLogin,

    // Scope Testing Actions
    enableScopeTesting,
    disableScopeTesting,
    updateTestingScopes
  }
})
